import { acquireAccessToken, invalidateAccessToken } from "@/lib/auth";
import type { Message } from "@/lib/api/types";
import { notifyAuthenticationRequired } from "@/lib/auth-events";
import { toast } from "@/hooks/use-toast";

const baseUrl = import.meta.env.VITE_API_URL;

export function urlBuilder(path: string) {
  return new URL(path.replace(/^\//, ""), baseUrl);
}

export function buildMessage({
  message: messageString,
  role = "user",
  image = null,
}: {
  message: string;
  // Optional, so the `= "user"` default above is actually reachable. It was
  // declared required, which made the default unusable and every call site repeat
  // the only value it can hold.
  role?: "user";
  image?: string | null;
}): Message {
  return {
    created_at: new Date().toISOString(),
    id: self.crypto.randomUUID(),
    role,
    content: {
      text: messageString,
      image,
    },
  };
}

/**
 * Why a request failed, in the only four ways that lead to different actions:
 *
 * - `auth` — no token could be obtained. Sign-in has to happen before anything
 *   else can.
 * - `unauthorized` — a token was obtained and the backend refused it (401/403).
 *   Also a sign-in problem, but a *different* one: the credentials exist and are
 *   not accepted.
 * - `server` — reached the backend, which answered with some other non-2xx.
 *   Retrying may work; signing in will not help.
 * - `network` — never reached the backend at all.
 */
export type RequestFailureKind = "auth" | "unauthorized" | "server" | "network";

export class RequestFailure extends Error {
  constructor(
    readonly kind: RequestFailureKind,
    message: string,
    readonly status?: number,
    // Declared as a field rather than passed to `super`: `Error`'s `cause`
    // option needs the ES2022 lib, which this project does not target.
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = "RequestFailure";
  }
}

/**
 * What the user is told, per kind.
 *
 * These were one message — "An error occurred while fetching data. Please try
 * again." — for every failure above. That is half the reason the Teams-desktop
 * 401 took two rounds to find: the tab showed a working chat, the toast blamed
 * data fetching, and nothing pointed at authentication. The real cause was
 * `Audience doesn't match` in the backend.
 *
 * Dutch, like the rest of the user-facing copy. The app used to be a mix — Dutch
 * sidebar, English toasts and placeholders — which is settled as all-Dutch in
 * MA-204. Console messages stay English: those are for us, not for users.
 */
const MESSAGES: Record<RequestFailureKind, string> = {
  auth: "Aanmelden is niet gelukt. Meld je opnieuw aan.",
  unauthorized: "Je sessie is niet langer geldig. Meld je opnieuw aan.",
  server: "De server kon het verzoek niet verwerken. Probeer het opnieuw.",
  network: "De server is niet bereikbaar. Controleer je verbinding en probeer het opnieuw.",
};

function fail(
  kind: RequestFailureKind,
  detail: string,
  status?: number,
  cause?: unknown,
): never {
  const failure = new RequestFailure(kind, detail, status, cause);
  console.error(`[api] request failed (${kind})`, failure);

  toast({
    title: "Fout",
    description: MESSAGES[kind],
    variant: "destructive",
  });

  // Both auth kinds mean the same thing to whoever can fix it: the user must
  // authenticate again. Announced here rather than left to each caller, because
  // every caller would have to remember, and the one that forgot would leave a
  // tab that looks signed in while nothing works.
  if (kind === "auth" || kind === "unauthorized") {
    notifyAuthenticationRequired();
  }

  throw failure;
}

/**
 * Fetch with an `Authorization: Bearer` header, failing in the four
 * distinguishable ways above.
 *
 * One retry, available to one path. When the backend refuses a token that came
 * from the host portal (MA-227), re-sending is worth exactly one attempt:
 * `invalidateAccessToken` drops the portal's token and latches that path off, so
 * the second `attempt()` acquires through MSAL instead and either succeeds or
 * produces the same `auth`/`unauthorized` failure the first one would have. On
 * every other build and path `invalidateAccessToken()` returns false and this
 * function behaves byte-for-byte as it did before -- one request, one toast, one
 * `notifyAuthenticationRequired`.
 *
 * The retry is gated on the invalidation returning true rather than on a
 * config flag, so the decision sits with the only module that knows whether
 * there were credentials worth discarding.
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  // Which of 401/403 the backend answered, recorded so the eventual failure still
  // carries the real status. They are the same problem to the user, but not to
  // whoever reads the console: 401 is a token the backend could not validate,
  // 403 one it validated and would not accept.
  let refusedStatus: number | undefined;

  // Acquire, send, check -- as one unit, because a retry has to redo all three.
  // Re-sending with the refused token would be pointless, and the whole purpose
  // of the retry is to pick up whatever `invalidateAccessToken` left in place.
  //
  // Returns the response on success, `undefined` on a 401/403 (the one outcome
  // the caller may retry), and throws via `fail` for everything else -- each step
  // gets its own try, rather than one around the whole function: a single catch
  // cannot tell a token failure from a dead connection, and it also swallows the
  // status check into the same bucket as both.
  const attempt = async (): Promise<Response | undefined> => {
    let token: string | undefined;
    try {
      token = await acquireAccessToken();
    } catch (e) {
      fail("auth", "token acquisition failed", undefined, e);
    }

    if (!token) {
      fail("auth", "no access token available (a redirect may be in progress)");
    }

    const headers = new Headers(init?.headers);
    headers.set("Authorization", `Bearer ${token}`);

    let response: Response;
    try {
      response = await fetch(input, { ...init, headers });
    } catch (e) {
      // `fetch` rejects only when the request never completed -- DNS, TLS,
      // offline, CORS preflight. A 500 is a resolved promise, so it cannot land
      // here.
      fail("network", "request did not reach the server", undefined, e);
    }

    if (response.status === 401 || response.status === 403) {
      refusedStatus = response.status;
      return undefined;
    }

    if (!response.ok) {
      fail("server", `backend returned ${response.status}`, response.status);
    }

    return response;
  };

  let response = await attempt();

  if (!response && invalidateAccessToken()) {
    response = await attempt();
  }

  if (!response) {
    fail("unauthorized", "backend refused the token", refusedStatus);
  }

  return response;
}

